import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import { FaPlus, FaSave, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import { signContentApi } from "../../api/services";
import { getErrorMessage } from "../../api/client";
import CmsImageField from "../../components/CmsImageField";
import { FormPageSkeleton } from "../../components/Skeleton";
import "./AdminHomeContent.css";

const TABS = [
  { key: "hero", label: "Hero" },
  { key: "about", label: "About" },
  { key: "services", label: "Services" },
  { key: "achievements", label: "Achievements" },
  { key: "work", label: "Our Work" },
  { key: "clients", label: "Clients" },
  { key: "cta", label: "CTA" },
];

const ICON_OPTIONS = [
  { value: "projects", label: "Projects" },
  { value: "users", label: "Users" },
  { value: "truck", label: "Delivery" },
];

const isSignUploadUrl = (url) =>
  typeof url === "string" && url.startsWith("/uploads/sign/");

const isBlobUrl = (url) => typeof url === "string" && url.startsWith("blob:");

const cloneContent = (value) => JSON.parse(JSON.stringify(value));

const collectSignImageUrls = (section) => {
  if (!section) return [];
  const urls = [];
  if (isSignUploadUrl(section.backgroundImage)) {
    urls.push(section.backgroundImage);
  }
  (section.items || []).forEach((item) => {
    if (isSignUploadUrl(item?.image)) urls.push(item.image);
  });
  return urls;
};

const emptyServiceItem = () => ({
  title: "",
  description: "Professional solutions designed for modern business branding.",
  sortOrder: 0,
  isActive: true,
});

const emptyAchievementItem = () => ({
  value: "",
  suffix: "",
  label: "",
  iconKey: "projects",
  animate: true,
  sortOrder: 0,
  isActive: true,
});

const emptyWorkItem = () => ({
  image: "",
  alt: "",
  sortOrder: 0,
  isActive: true,
});

const emptyClientItem = () => ({
  title: "",
  image: "",
  sortOrder: 0,
  isActive: true,
});

const AdminSignContent = () => {
  const [activeTab, setActiveTab] = useState("hero");
  const [content, setContent] = useState(null);
  const [savedContent, setSavedContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const pendingFilesRef = useRef(new Map());

  const setPendingFile = useCallback((fieldKey, file) => {
    if (file) pendingFilesRef.current.set(fieldKey, file);
    else pendingFilesRef.current.delete(fieldKey);
  }, []);

  const clearPendingKeysForSection = useCallback((sectionKey) => {
    const map = pendingFilesRef.current;
    [...map.keys()]
      .filter((key) => key === sectionKey || key.startsWith(`${sectionKey}.`))
      .forEach((key) => map.delete(key));
  }, []);

  const reindexItemPending = useCallback((sectionKey, removedIndex) => {
    const map = pendingFilesRef.current;
    const prefix = `${sectionKey}.items.`;
    const suffix = ".image";
    map.delete(`${prefix}${removedIndex}${suffix}`);

    const higher = [...map.entries()]
      .map(([key, file]) => {
        if (!key.startsWith(prefix) || !key.endsWith(suffix)) return null;
        const index = Number(key.slice(prefix.length, -suffix.length));
        if (Number.isNaN(index) || index <= removedIndex) return null;
        return { index, file, key };
      })
      .filter(Boolean)
      .sort((a, b) => a.index - b.index);

    higher.forEach(({ key }) => map.delete(key));
    higher.forEach(({ index, file }) => {
      map.set(`${prefix}${index - 1}${suffix}`, file);
    });
  }, []);

  const uploadPendingForSection = useCallback(async (sectionKey, sectionData) => {
    const data = {
      ...sectionData,
      items: sectionData.items
        ? sectionData.items.map((item) => ({ ...item }))
        : sectionData.items,
      paragraphs: sectionData.paragraphs
        ? [...sectionData.paragraphs]
        : sectionData.paragraphs,
    };
    const pending = pendingFilesRef.current;

    if (sectionKey === "hero" && pending.has("hero.backgroundImage")) {
      const res = await signContentApi.uploadImages([
        pending.get("hero.backgroundImage"),
      ]);
      const url = res.data.data.urls?.[0] || "";
      if (isBlobUrl(data.backgroundImage)) URL.revokeObjectURL(data.backgroundImage);
      data.backgroundImage = url;
      pending.delete("hero.backgroundImage");
    } else if (isBlobUrl(data.backgroundImage)) {
      URL.revokeObjectURL(data.backgroundImage);
      data.backgroundImage = "";
    }

    if (Array.isArray(data.items)) {
      for (let i = 0; i < data.items.length; i += 1) {
        const fieldKey = `${sectionKey}.items.${i}.image`;
        if (pending.has(fieldKey)) {
          const res = await signContentApi.uploadImages([pending.get(fieldKey)]);
          const url = res.data.data.urls?.[0] || "";
          if (isBlobUrl(data.items[i].image)) {
            URL.revokeObjectURL(data.items[i].image);
          }
          data.items[i].image = url;
          pending.delete(fieldKey);
        } else if (isBlobUrl(data.items[i].image)) {
          URL.revokeObjectURL(data.items[i].image);
          data.items[i].image = "";
        }
      }
    }

    return data;
  }, []);

  const deleteReplacedSignImages = useCallback(async (previousSection, nextSection) => {
    const previous = new Set(collectSignImageUrls(previousSection));
    const next = new Set(collectSignImageUrls(nextSection));
    const removals = [...previous].filter((url) => !next.has(url));
    await Promise.all(
      removals.map(async (url) => {
        try {
          await signContentApi.deleteImage(url);
        } catch {
          // ignore cleanup failures
        }
      })
    );
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await signContentApi.get();
      const loaded = res.data.data.content;
      setContent(loaded);
      setSavedContent(cloneContent(loaded));
      pendingFilesRef.current.clear();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to load Sign & Graphics content"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patch = (section, value) => {
    setContent((prev) => ({ ...prev, [section]: value }));
  };

  const saveSection = async (sectionKey) => {
    if (!content) return;
    setSaving(true);
    try {
      let sectionData = { ...content[sectionKey] };
      if (["hero", "work", "clients"].includes(sectionKey)) {
        sectionData = await uploadPendingForSection(sectionKey, sectionData);
      }

      const previousSection = savedContent?.[sectionKey];
      const res = await signContentApi.update({ [sectionKey]: sectionData });
      const nextContent = res.data.data.content;
      await deleteReplacedSignImages(previousSection, nextContent[sectionKey]);
      clearPendingKeysForSection(sectionKey);
      setContent(nextContent);
      setSavedContent(cloneContent(nextContent));
      toast.success("Section saved");
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  const updateItem = (sectionKey, index, field, value) => {
    const section = content[sectionKey];
    const items = [...(section.items || [])];
    items[index] = { ...items[index], [field]: value };
    patch(sectionKey, { ...section, items });
  };

  const addItem = (sectionKey, factory) => {
    const section = content[sectionKey];
    const items = [...(section.items || []), factory()];
    items.forEach((item, i) => {
      item.sortOrder = i;
    });
    patch(sectionKey, { ...section, items });
  };

  const removeItem = (sectionKey, index) => {
    const section = content[sectionKey];
    const removed = section.items?.[index];
    if (isBlobUrl(removed?.image)) {
      URL.revokeObjectURL(removed.image);
    }
    reindexItemPending(sectionKey, index);
    const items = (section.items || []).filter((_, i) => i !== index);
    patch(sectionKey, { ...section, items });
    toast.info("Item removed — click Save to apply");
  };

  const updateParagraph = (index, value) => {
    const paragraphs = [...(content.about.paragraphs || [])];
    paragraphs[index] = value;
    patch("about", { ...content.about, paragraphs });
  };

  const addParagraph = () => {
    const paragraphs = [...(content.about.paragraphs || []), ""];
    patch("about", { ...content.about, paragraphs });
  };

  const removeParagraph = (index) => {
    const paragraphs = (content.about.paragraphs || []).filter((_, i) => i !== index);
    patch("about", { ...content.about, paragraphs });
  };

  if (loading || !content) {
    return (
      <div className="manage-page">
        <FormPageSkeleton />
      </div>
    );
  }

  return (
    <div className="manage-page">
      <header className="manage-head">
        <div>
          <h1>Sign & Graphics page</h1>
          <p>Edit hero, about, services, achievements, portfolio, clients, and CTA.</p>
        </div>
      </header>

      <div className="home-cms-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`home-cms-tab ${activeTab === tab.key ? "active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="home-cms-card">
        {activeTab === "hero" && (
          <>
            <CmsImageField
              label="Background image"
              value={content.hero.backgroundImage}
              onChange={(v) => patch("hero", { ...content.hero, backgroundImage: v })}
              onPendingFile={(file) => setPendingFile("hero.backgroundImage", file)}
            />
            <Form.Group className="mb-3">
              <Form.Label>Badge</Form.Label>
              <Form.Control
                value={content.hero.badge || ""}
                onChange={(e) => patch("hero", { ...content.hero, badge: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                value={content.hero.title || ""}
                onChange={(e) => patch("hero", { ...content.hero, title: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Subtitle</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={content.hero.subtitle || ""}
                onChange={(e) =>
                  patch("hero", { ...content.hero, subtitle: e.target.value })
                }
              />
            </Form.Group>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Form.Label>Primary button text</Form.Label>
                <Form.Control
                  value={content.hero.primaryButtonText || ""}
                  onChange={(e) =>
                    patch("hero", {
                      ...content.hero,
                      primaryButtonText: e.target.value,
                    })
                  }
                />
              </div>
              <div className="col-md-6 mb-3">
                <Form.Label>Primary button link</Form.Label>
                <Form.Control
                  value={content.hero.primaryButtonLink || ""}
                  onChange={(e) =>
                    patch("hero", {
                      ...content.hero,
                      primaryButtonLink: e.target.value,
                    })
                  }
                />
              </div>
              <div className="col-md-6 mb-3">
                <Form.Label>Secondary button text</Form.Label>
                <Form.Control
                  value={content.hero.secondaryButtonText || ""}
                  onChange={(e) =>
                    patch("hero", {
                      ...content.hero,
                      secondaryButtonText: e.target.value,
                    })
                  }
                />
              </div>
              <div className="col-md-6 mb-3">
                <Form.Label>Secondary button link</Form.Label>
                <Form.Control
                  value={content.hero.secondaryButtonLink || ""}
                  onChange={(e) =>
                    patch("hero", {
                      ...content.hero,
                      secondaryButtonLink: e.target.value,
                    })
                  }
                  placeholder="#sign-services"
                />
              </div>
            </div>
            <div className="home-cms-actions">
              <Button variant="dark" disabled={saving} onClick={() => saveSection("hero")}>
                {saving ? <Spinner size="sm" animation="border" /> : <FaSave />} Save hero
              </Button>
            </div>
          </>
        )}

        {activeTab === "about" && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Label</Form.Label>
              <Form.Control
                value={content.about.label || ""}
                onChange={(e) =>
                  patch("about", { ...content.about, label: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                value={content.about.title || ""}
                onChange={(e) =>
                  patch("about", { ...content.about, title: e.target.value })
                }
              />
            </Form.Group>
            <Form.Label className="mb-2">Paragraphs</Form.Label>
            {(content.about.paragraphs || []).map((paragraph, index) => (
              <div className="home-cms-item" key={index}>
                <div className="home-cms-item-head">
                  <h4>Paragraph {index + 1}</h4>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => removeParagraph(index)}
                  >
                    <FaTrash />
                  </Button>
                </div>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={paragraph}
                  onChange={(e) => updateParagraph(index, e.target.value)}
                />
              </div>
            ))}
            <Button variant="outline-dark" className="mb-3" onClick={addParagraph}>
              <FaPlus /> Add paragraph
            </Button>
            <Form.Group className="mb-3">
              <Form.Label>What we deliver — title</Form.Label>
              <Form.Control
                value={content.about.cardTitle || ""}
                onChange={(e) =>
                  patch("about", { ...content.about, cardTitle: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>What we deliver — description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={content.about.cardDescription || ""}
                onChange={(e) =>
                  patch("about", {
                    ...content.about,
                    cardDescription: e.target.value,
                  })
                }
              />
            </Form.Group>
            <div className="home-cms-actions">
              <Button variant="dark" disabled={saving} onClick={() => saveSection("about")}>
                {saving ? <Spinner size="sm" animation="border" /> : <FaSave />} Save about
              </Button>
            </div>
          </>
        )}

        {activeTab === "services" && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Subtitle</Form.Label>
              <Form.Control
                value={content.services.subtitle || ""}
                onChange={(e) =>
                  patch("services", {
                    ...content.services,
                    subtitle: e.target.value,
                  })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                value={content.services.title || ""}
                onChange={(e) =>
                  patch("services", { ...content.services, title: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={content.services.description || ""}
                onChange={(e) =>
                  patch("services", {
                    ...content.services,
                    description: e.target.value,
                  })
                }
              />
            </Form.Group>

            {(content.services.items || []).map((item, index) => (
              <div className="home-cms-item" key={index}>
                <div className="home-cms-item-head">
                  <h4>Service {index + 1}</h4>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => removeItem("services", index)}
                  >
                    <FaTrash />
                  </Button>
                </div>
                <Form.Group className="mb-2">
                  <Form.Label>Title</Form.Label>
                  <Form.Control
                    value={item.title || ""}
                    onChange={(e) =>
                      updateItem("services", index, "title", e.target.value)
                    }
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={item.description || ""}
                    onChange={(e) =>
                      updateItem("services", index, "description", e.target.value)
                    }
                  />
                </Form.Group>
                <Form.Check
                  type="switch"
                  id={`service-active-${index}`}
                  label="Active"
                  checked={item.isActive !== false}
                  onChange={(e) =>
                    updateItem("services", index, "isActive", e.target.checked)
                  }
                />
              </div>
            ))}

            <Button
              variant="outline-dark"
              className="mb-3"
              onClick={() => addItem("services", emptyServiceItem)}
            >
              <FaPlus /> Add service
            </Button>
            <div className="home-cms-actions">
              <Button
                variant="dark"
                disabled={saving}
                onClick={() => saveSection("services")}
              >
                {saving ? <Spinner size="sm" animation="border" /> : <FaSave />}{" "}
                Save services
              </Button>
            </div>
          </>
        )}

        {activeTab === "achievements" && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Subtitle</Form.Label>
              <Form.Control
                value={content.achievements.subtitle || ""}
                onChange={(e) =>
                  patch("achievements", {
                    ...content.achievements,
                    subtitle: e.target.value,
                  })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                value={content.achievements.title || ""}
                onChange={(e) =>
                  patch("achievements", {
                    ...content.achievements,
                    title: e.target.value,
                  })
                }
              />
            </Form.Group>

            {(content.achievements.items || []).map((item, index) => (
              <div className="home-cms-item" key={index}>
                <div className="home-cms-item-head">
                  <h4>Stat {index + 1}</h4>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => removeItem("achievements", index)}
                  >
                    <FaTrash />
                  </Button>
                </div>
                <div className="row">
                  <div className="col-md-4 mb-2">
                    <Form.Label>Value</Form.Label>
                    <Form.Control
                      value={item.value || ""}
                      onChange={(e) =>
                        updateItem("achievements", index, "value", e.target.value)
                      }
                      placeholder="1000 or UAE"
                    />
                  </div>
                  <div className="col-md-2 mb-2">
                    <Form.Label>Suffix</Form.Label>
                    <Form.Control
                      value={item.suffix || ""}
                      onChange={(e) =>
                        updateItem("achievements", index, "suffix", e.target.value)
                      }
                      placeholder="+"
                    />
                  </div>
                  <div className="col-md-6 mb-2">
                    <Form.Label>Label</Form.Label>
                    <Form.Control
                      value={item.label || ""}
                      onChange={(e) =>
                        updateItem("achievements", index, "label", e.target.value)
                      }
                    />
                  </div>
                  <div className="col-md-6 mb-2">
                    <Form.Label>Icon</Form.Label>
                    <Form.Select
                      value={item.iconKey || "projects"}
                      onChange={(e) =>
                        updateItem("achievements", index, "iconKey", e.target.value)
                      }
                    >
                      {ICON_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className="col-md-6 mb-2 d-flex align-items-end">
                    <Form.Check
                      type="switch"
                      id={`achieve-animate-${index}`}
                      label="Animate number count-up"
                      checked={item.animate !== false}
                      onChange={(e) =>
                        updateItem("achievements", index, "animate", e.target.checked)
                      }
                    />
                  </div>
                </div>
                <Form.Check
                  type="switch"
                  id={`achieve-active-${index}`}
                  label="Active"
                  checked={item.isActive !== false}
                  onChange={(e) =>
                    updateItem("achievements", index, "isActive", e.target.checked)
                  }
                />
              </div>
            ))}

            <Button
              variant="outline-dark"
              className="mb-3"
              onClick={() => addItem("achievements", emptyAchievementItem)}
            >
              <FaPlus /> Add achievement
            </Button>
            <div className="home-cms-actions">
              <Button
                variant="dark"
                disabled={saving}
                onClick={() => saveSection("achievements")}
              >
                {saving ? <Spinner size="sm" animation="border" /> : <FaSave />}{" "}
                Save achievements
              </Button>
            </div>
          </>
        )}

        {activeTab === "work" && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Subtitle</Form.Label>
              <Form.Control
                value={content.work.subtitle || ""}
                onChange={(e) =>
                  patch("work", { ...content.work, subtitle: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                value={content.work.title || ""}
                onChange={(e) =>
                  patch("work", { ...content.work, title: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={content.work.description || ""}
                onChange={(e) =>
                  patch("work", { ...content.work, description: e.target.value })
                }
              />
            </Form.Group>

            {(content.work.items || []).map((item, index) => (
              <div className="home-cms-item" key={index}>
                <div className="home-cms-item-head">
                  <h4>Image {index + 1}</h4>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => removeItem("work", index)}
                  >
                    <FaTrash />
                  </Button>
                </div>
                <CmsImageField
                  label="Portfolio image"
                  value={item.image}
                  onChange={(v) => updateItem("work", index, "image", v)}
                  onPendingFile={(file) =>
                    setPendingFile(`work.items.${index}.image`, file)
                  }
                />
                <Form.Group className="mb-2">
                  <Form.Label>Alt text</Form.Label>
                  <Form.Control
                    value={item.alt || ""}
                    onChange={(e) =>
                      updateItem("work", index, "alt", e.target.value)
                    }
                  />
                </Form.Group>
                <Form.Check
                  type="switch"
                  id={`work-active-${index}`}
                  label="Active"
                  checked={item.isActive !== false}
                  onChange={(e) =>
                    updateItem("work", index, "isActive", e.target.checked)
                  }
                />
              </div>
            ))}

            <Button
              variant="outline-dark"
              className="mb-3"
              onClick={() => addItem("work", emptyWorkItem)}
            >
              <FaPlus /> Add image
            </Button>
            <div className="home-cms-actions">
              <Button variant="dark" disabled={saving} onClick={() => saveSection("work")}>
                {saving ? <Spinner size="sm" animation="border" /> : <FaSave />} Save work
              </Button>
            </div>
          </>
        )}

        {activeTab === "clients" && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Subtitle</Form.Label>
              <Form.Control
                value={content.clients.subtitle || ""}
                onChange={(e) =>
                  patch("clients", {
                    ...content.clients,
                    subtitle: e.target.value,
                  })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                value={content.clients.title || ""}
                onChange={(e) =>
                  patch("clients", { ...content.clients, title: e.target.value })
                }
              />
            </Form.Group>

            {(content.clients.items || []).map((item, index) => (
              <div className="home-cms-item" key={index}>
                <div className="home-cms-item-head">
                  <h4>Client {index + 1}</h4>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => removeItem("clients", index)}
                  >
                    <FaTrash />
                  </Button>
                </div>
                <Form.Group className="mb-2">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    value={item.title || ""}
                    onChange={(e) =>
                      updateItem("clients", index, "title", e.target.value)
                    }
                  />
                </Form.Group>
                <CmsImageField
                  label="Logo (optional — shows name if empty)"
                  value={item.image}
                  onChange={(v) => updateItem("clients", index, "image", v)}
                  onPendingFile={(file) =>
                    setPendingFile(`clients.items.${index}.image`, file)
                  }
                />
                <Form.Check
                  type="switch"
                  id={`client-active-${index}`}
                  label="Active"
                  checked={item.isActive !== false}
                  onChange={(e) =>
                    updateItem("clients", index, "isActive", e.target.checked)
                  }
                />
              </div>
            ))}

            <Button
              variant="outline-dark"
              className="mb-3"
              onClick={() => addItem("clients", emptyClientItem)}
            >
              <FaPlus /> Add client
            </Button>
            <div className="home-cms-actions">
              <Button
                variant="dark"
                disabled={saving}
                onClick={() => saveSection("clients")}
              >
                {saving ? <Spinner size="sm" animation="border" /> : <FaSave />}{" "}
                Save clients
              </Button>
            </div>
          </>
        )}

        {activeTab === "cta" && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                value={content.cta.title || ""}
                onChange={(e) =>
                  patch("cta", { ...content.cta, title: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={content.cta.description || ""}
                onChange={(e) =>
                  patch("cta", { ...content.cta, description: e.target.value })
                }
              />
            </Form.Group>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Form.Label>Button text</Form.Label>
                <Form.Control
                  value={content.cta.buttonText || ""}
                  onChange={(e) =>
                    patch("cta", { ...content.cta, buttonText: e.target.value })
                  }
                />
              </div>
              <div className="col-md-6 mb-3">
                <Form.Label>Button link</Form.Label>
                <Form.Control
                  value={content.cta.buttonLink || ""}
                  onChange={(e) =>
                    patch("cta", { ...content.cta, buttonLink: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="home-cms-actions">
              <Button variant="dark" disabled={saving} onClick={() => saveSection("cta")}>
                {saving ? <Spinner size="sm" animation="border" /> : <FaSave />} Save CTA
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminSignContent;
